import { supabase, updateCampCategories } from './supabase';
import { enrichCampData } from './aiEnrichmentClient';

export interface EnrichExistingCampRequest {
  campId: string;
  website?: string;
}

export interface EnrichExistingCampResponse {
  success: boolean;
  message: string;
  enrichedData?: {
    updateData: any;
    enrichmentResponse: any;
    originalCamp: any;
  };
}

/**
 * Save enriched data to database after user approval in review step
 */
export async function saveEnrichedCamp(
  campId: string,
  updateData: any,
  enrichmentResponse?: any
): Promise<{ success: boolean; message: string }> {
  try {
    const { error: updateError } = await supabase
      .from('camps')
      .update(updateData)
      .eq('id', campId);

    if (updateError) {
      return {
        success: false,
        message: `Failed to save enriched data: ${updateError.message}`
      };
    }

    // Save categories to camp_category_relations if suggested_categories exist
    if (enrichmentResponse?.suggested_categories || updateData.suggested_categories) {
      const suggestedCategories = enrichmentResponse?.suggested_categories || updateData.suggested_categories;
      try {
        const categoryIds = await convertSuggestedCategoriesToIds(suggestedCategories);
        console.log('🏷️ Category IDs to save:', categoryIds);
        
        if (categoryIds.length > 0) {
          const categorySuccess = await updateCampCategories(campId, categoryIds);
          if (categorySuccess) {
            console.log('✅ Categories saved successfully');
          } else {
            console.warn('⚠️ Failed to save categories');
          }
        } else {
          console.log('⚠️ No valid categories found to save');
        }
      } catch (categoryError) {
        console.error('❌ Error saving categories:', categoryError);
        // Don't fail the whole operation if categories fail
      }
    }

    return {
      success: true,
      message: 'Camp enriched successfully'
    };
  } catch (error) {
    console.error('Error saving enriched camp:', error);
    return {
      success: false,
      message: `Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

// Helper function to convert suggested_categories to category IDs
async function convertSuggestedCategoriesToIds(suggestedCategories: any): Promise<string[]> {
  const categoryIds: string[] = [];
  
  try {
    // Handle different possible structures of suggested_categories
    const allCategoryNames: string[] = [];
    
    if (typeof suggestedCategories === 'object' && suggestedCategories !== null) {
      // If it's an object with category type keys (focus_area, format, etc.)
      Object.values(suggestedCategories).forEach((categoryArray: any) => {
        if (Array.isArray(categoryArray)) {
          categoryArray.forEach((categoryName: string) => {
            if (typeof categoryName === 'string' && categoryName.trim()) {
              allCategoryNames.push(categoryName.trim());
            }
          });
        }
      });
    }
    
    console.log('🏷️ Category names to look up:', allCategoryNames);
    
    if (allCategoryNames.length === 0) {
      return [];
    }
    
    // Fetch all categories from database
    const { data: allCategories, error: fetchError } = await supabase
      .from('camp_categories')
      .select('id, name');
    
    if (fetchError) {
      console.error('Error fetching categories:', fetchError);
      return [];
    }
    
    // Match category names (case-insensitive)
    allCategoryNames.forEach(categoryName => {
      const matchedCategory = allCategories?.find(
        cat => cat.name.toLowerCase() === categoryName.toLowerCase()
      );
      if (matchedCategory) {
        categoryIds.push(matchedCategory.id);
      } else {
        console.warn(`⚠️ Category not found in database: "${categoryName}"`);
      }
    });
    
    console.log('🏷️ Matched category IDs:', categoryIds);
    return categoryIds;
  } catch (error) {
    console.error('Error converting suggested categories:', error);
    return [];
  }
}

export async function enrichExistingCamp(
  request: EnrichExistingCampRequest,
  onProgress?: (message: string) => void
): Promise<EnrichExistingCampResponse> {
  try {
    onProgress?.('🚀 Initializing AI enrichment...');
    
    // Get the existing camp data
    onProgress?.('📋 Fetching camp data from database...');
    const { data: camp, error: fetchError } = await supabase
      .from('camps')
      .select('*')
      .eq('id', request.campId)
      .single();

    if (fetchError || !camp) {
      return {
        success: false,
        message: 'Camp not found'
      };
    }

    onProgress?.('Starting AI enrichment...');

    // Prepare enrichment request
    const enrichmentRequest = {
      campName: camp.name,
      website: request.website || camp.website_url || undefined,
      description: camp.description || undefined,
      address: camp.address || undefined,
      city: camp.city || undefined,
      state: camp.state || undefined,
      zipCode: camp.zip_code || undefined,
      latitude: camp.latitude || undefined,
      longitude: camp.longitude || undefined,
      email: camp.email || undefined,
      phone: camp.phone || undefined,
      slug: camp.slug || undefined,
      suggested_images: camp.suggested_images || undefined,
      contactInfo: camp.phone || undefined,
      facebookActivities: camp.facebookActivities || undefined,
      facebookAmenities: camp.facebookAmenities || undefined,
      facebookPricing: camp.facebookPricing || undefined,
      facebookDates: camp.facebookDates || undefined,
      imageAnalysis: camp.enrichment_metadata?.image_analysis?.map((img: any) => ({
        url: img.url,
        quality: img.quality,
        relevance: img.relevance,
        description: "Camp image",
        issues: img.issues,
      })) || undefined,
      campPagesData: undefined as any,
    };

    // Call the enrichment API
    onProgress?.('🤖 Calling AI enrichment API...');
    const enrichmentResponse = await enrichCampData(enrichmentRequest, onProgress);

    if (!enrichmentResponse) {
      return {
        success: false,
        message: 'Enrichment failed - no response received'
      };
    }

    onProgress?.('✅ Enrichment data extracted successfully!');

    // Prepare update data (but don't save yet - wait for user review/approval)
    // This will be used in the review step to show old vs new
    const updateData = {
      description: enrichmentResponse.description || camp.description,
      short_description: enrichmentResponse.short_description || camp.short_description,
      min_age: enrichmentResponse.min_age || camp.min_age,
      max_age: enrichmentResponse.max_age || camp.max_age,
      start_hour_1: enrichmentResponse.start_hour_1 || camp.start_hour_1,
      finish_hour_1: enrichmentResponse.finish_hour_1 || camp.finish_hour_1,
      start_hour_2: enrichmentResponse.start_hour_2 || camp.start_hour_2,
      finish_hour_2: enrichmentResponse.finish_hour_2 || camp.finish_hour_2,
      extended_time_available: enrichmentResponse.extended_time_available ?? camp.extended_time_available,
      lunch_provided: enrichmentResponse.lunch_provided ?? camp.lunch_provided,
      lunch_fee: enrichmentResponse.lunch_fee || camp.lunch_fee,
      five_day_cost: enrichmentResponse.five_day_cost || camp.five_day_cost,
      dates_available: enrichmentResponse.dates_available || camp.dates_available,
      notes: enrichmentResponse.notes || camp.notes,
      activities: enrichmentResponse.activities || camp.activities,
      amenities: enrichmentResponse.amenities || camp.amenities,
      email: enrichmentResponse.email || camp.email,
      address: enrichmentResponse.address || camp.address,
      phone: enrichmentResponse.phone || camp.phone,
      latitude: enrichmentResponse.latitude || camp.latitude,
      longitude: enrichmentResponse.longitude || camp.longitude,
      blocking_reason: enrichmentResponse.blocking_reason || camp.blocking_reason,
      insufficient_content: enrichmentResponse.insufficient_content ?? camp.insufficient_content,
      // Save social_media directly to database column (like import flow does)
      social_media: enrichmentResponse.social_media || camp.social_media || null,
      suggested_categories: enrichmentResponse.suggested_categories || camp.suggested_categories || null,
      ai_enriched: {
        enrichments: [
          ...(camp.ai_enriched?.enrichments || []),
          {
            timestamp: new Date().toISOString(),
            version: 'v2',
            tokens_used: enrichmentResponse.enrichment_metadata?.tokens_used || 0,
            cost: enrichmentResponse.enrichment_metadata?.cost || 0,
            agent: enrichmentResponse.enrichment_metadata?.agent || 'gpt-4',
            model: enrichmentResponse.enrichment_metadata?.model || 'gpt-4',
            enrichment_mode: 'manual'
          }
        ],
        total_enrichments: (camp.ai_enriched?.total_enrichments || 0) + 1,
        last_enriched: new Date().toISOString()
      }
    };
    
    // Return data for review step - don't save yet
    onProgress?.('📋 Ready for review - please compare old vs new data');
    
    return {
      success: true,
      message: 'Enrichment completed - ready for review',
      enrichedData: {
        updateData,
        enrichmentResponse: enrichmentResponse, // Include full enrichment response for file export
        originalCamp: camp // Include original camp data for comparison
      }
    };

  } catch (error) {
    console.error('Error enriching existing camp:', error);
    return {
      success: false,
      message: `Enrichment failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}
